/**
 * BlogIndexPage — paginated list of published blog posts.
 *
 * The component manages its own state (offset/posts/loading/error) but
 * delegates the actual fetch to the consumer-supplied `client.listBlogPosts`.
 * Renders 4 states:
 *   - loading      → loadingFallback (or null)
 *   - error        → errorFallback({ error, retry }) or default banner
 *   - empty        → emptyFallback or a default message
 *   - ready        → grid of BlogPostCard + pagination
 *
 * @module @puffin-core/marketing-shell/blog/BlogIndexPage
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BlogPostCard from './BlogPostCard.jsx';
import BlogPagination from './BlogPagination.jsx';
import { DEFAULT_BLOG_UI_STRINGS } from './blogUiStrings.js';

/**
 * Build the URL for a 1-indexed index page under `hrefPrefix`.
 *
 * WS-J — page 1 is canonically the bare prefix (`/blog`), NOT `/blog/page/1`.
 * The server rejects `/blog/page/1` for the same reason: one URL per page of
 * results, so page 1 never has two addresses serving identical content.
 */
export function buildBlogPageHref(hrefPrefix, page) {
  return page <= 1 ? hrefPrefix : `${hrefPrefix}/page/${page}`;
}

/**
 * useBlogPagination — the offset cursor + its two step actions. `pageSize` is
 * the step; the offset never goes below zero.
 *
 * WS-J — paging now also moves the URL. `history.pushState` (rather than a full
 * navigation) keeps the SPA feel while giving each page a real, shareable
 * address; a `popstate` listener maps Back/Forward back onto the offset so the
 * browser's history buttons step pages instead of leaving the view frozen on the
 * page the visitor last clicked to.
 */
function useBlogPagination({ initialOffset, pageSize, hrefPrefix }) {
  const [offset, setOffset] = useState(initialOffset);

  // Guarded for SSR: this component also renders inside `renderBlogHtml`, where
  // there is no window/history.
  const pushPageUrl = useCallback(
    (nextOffset) => {
      if (typeof window === 'undefined' || !window.history) return;
      const page = Math.floor(nextOffset / pageSize) + 1;
      window.history.pushState({ blogOffset: nextOffset }, '', buildBlogPageHref(hrefPrefix, page));
    },
    [pageSize, hrefPrefix],
  );

  const onPrev = useCallback(() => {
    setOffset((o) => {
      const next = Math.max(0, o - pageSize);
      pushPageUrl(next);
      return next;
    });
  }, [pageSize, pushPageUrl]);
  const onNext = useCallback(() => {
    setOffset((o) => {
      const next = o + pageSize;
      pushPageUrl(next);
      return next;
    });
  }, [pageSize, pushPageUrl]);

  useEffect(() => {
    // No `typeof window` guard: effects do not run during server rendering, so
    // that branch would be unreachable — dead code the coverage floor would then
    // have to be lied to about. `pushPageUrl` above DOES guard, because it is
    // reachable with a stubbed-out history.
    //
    // Derive from the PATH, not from history.state: a visitor can arrive on
    // `/blog/page/3` via a shared link, where state is null but the path is
    // authoritative.
    const onPopState = () => {
      const match = window.location.pathname.match(/\/page\/(\d+)\/?$/);
      const page = match ? Number(match[1]) : 1;
      setOffset(page > 1 ? (page - 1) * pageSize : 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [pageSize]);

  return { offset, onPrev, onNext };
}

/**
 * useBlogPostFetch — the loading/ready/error machine around
 * `client.listBlogPosts` for a single offset window, plus the retry action.
 * Always requests `pageSize + 1` rows so the caller can derive `hasMore`
 * without a second count query.
 */
function useBlogPostFetch({ client, pageSize, offset, locale, initialPosts }) {
  const [state, setState] = useState(initialPosts ? 'ready' : 'loading');
  const [posts, setPosts] = useState(initialPosts ?? []);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // BLOG-STD-001: server-rendered index pages arrive seeded from `initialPosts`
  // (the listBlogPosts result for `initialOffset`), so skip the redundant first
  // fetch to avoid a hydration loading-flash. Pagination + retry fetch normally.
  const seededRef = useRef(Boolean(initialPosts));

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return undefined;
    }
    let alive = true;
    setState('loading');
    setError(null);
    client
      .listBlogPosts({ limit: pageSize + 1, offset, locale })
      .then((rows) => {
        if (!alive) return;
        setPosts(rows);
        setState('ready');
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setState('error');
      });
    return () => {
      alive = false;
    };
  }, [client, pageSize, offset, reloadToken, locale]);

  const retry = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  return { state, posts, error, retry };
}

/**
 * useBlogPostList — the index page's whole data layer: the paged cursor
 * composed with the fetch machine that follows it.
 */
function useBlogPostList({ client, pageSize, initialPosts, initialOffset, locale, hrefPrefix }) {
  const { offset, onPrev, onNext } = useBlogPagination({ initialOffset, pageSize, hrefPrefix });
  const { state, posts, error, retry } = useBlogPostFetch({
    client,
    pageSize,
    offset,
    locale,
    initialPosts,
  });

  return { offset, state, posts, error, onPrev, onNext, retry };
}

/**
 * The blog's index page. Props are true to `BlogIndexPage.propTypes` (PROTECTOR-SMS-001 U1b: the server render through
 * `blogPageElementFor` passes only what it has, so an optional prop must read as optional).
 *
 * @param {Object} props
 * @param {{listBlogPosts: Function}} props.client
 * @param {Array} [props.initialPosts] - the posts the server seeded
 * @param {number} [props.initialOffset=0]
 * @param {number} [props.initialPage=1]
 * @param {number} [props.pageSize=12]
 * @param {number} [props.totalKnownCount] - the corpus total the server seeded
 * @param {string} [props.heading]
 * @param {string} [props.subheading]
 * @param {import('react').ReactNode} [props.loadingFallback=null]
 * @param {Function} [props.errorFallback] - `({ error, retry }) => node`
 * @param {import('react').ReactNode} [props.emptyFallback]
 * @param {string} [props.readingMinutesLabel]
 * @param {string} [props.hrefPrefix='/blog']
 * @param {string} [props.locale='en'] - the content locale
 * @param {Object} [props.strings] - the blog's chrome strings (`getBlogUiStrings`)
 * @returns {import('react').ReactNode} the page, or the fallback its state calls for
 */
export default function BlogIndexPage({
  client,
  initialPosts,
  initialOffset = 0,
  // WS-J — 1-indexed page from the URL (`/blog/page/:page`). Used only when no
  // SSR seed supplied an explicit `initialOffset`: the seed is cleared after the
  // first commit, so a later in-SPA navigation to page 3 has no seed and would
  // otherwise render page 1's posts under a page-3 URL.
  initialPage = 1,
  pageSize = 12,
  // SEO-CRAWL-001 — the corpus total, seeded by the server. Turns the control's
  // label from "Page 3" into "Page 3 of 35" and is what lets a reader see where
  // the end is. The client cannot derive it: it only ever holds one window.
  totalKnownCount,
  heading,
  subheading,
  loadingFallback = null,
  errorFallback,
  emptyFallback,
  readingMinutesLabel,
  hrefPrefix = '/blog',
  // MLC-001: `locale` is the active CONTENT locale (the i18n short tag threaded
  // by BlogTemplateRoute, e.g. 'en' | 'es' | 'es-mx'). It is sent verbatim to
  // `listBlogPosts` so the index lists native rows for that language, and is
  // reused for date display. Defaults to 'en' (the model's own default), so EN
  // behavior is byte-identical.
  locale = 'en',
  strings,
}) {
  // BLOG-STD-001: chrome strings come from the ecosystem SSOT (getBlogUiStrings),
  // threaded by BlogTemplateRoute (client) + renderBlogHtml (server) so both
  // render identical localized markup. Absent `strings` → English defaults, so
  // standalone rendering + existing callers are byte-identical.
  const s = strings ? { ...DEFAULT_BLOG_UI_STRINGS, ...strings } : DEFAULT_BLOG_UI_STRINGS;
  const resolvedHeading = heading ?? s.indexHeading;
  const resolvedReadingMinutesLabel = readingMinutesLabel ?? s.readingMinutesLabel;
  const resolvedInitialOffset =
    initialOffset || (initialPage > 1 ? (initialPage - 1) * pageSize : 0);
  const { offset, state, posts, error, onPrev, onNext, retry } = useBlogPostList({
    client,
    pageSize,
    initialPosts,
    initialOffset: resolvedInitialOffset,
    locale,
    hrefPrefix,
  });

  if (state === 'loading') return loadingFallback;

  if (state === 'error') {
    if (typeof errorFallback === 'function') return errorFallback({ error, retry });
    return (
      <section className="section-marketing" role="alert" aria-live="assertive">
        <h2 className="section-heading">{s.errorHeading}</h2>
        <p className="section-description">{s.errorBodyIndex}</p>
        <button type="button" className="btn btn-marketing-primary" onClick={retry}>
          {s.retry}
        </button>
      </section>
    );
  }

  // Ready. We requested `pageSize + 1` so we can tell whether the next
  // page exists without a separate count query.
  const hasMore = posts.length > pageSize;
  const display = hasMore ? posts.slice(0, pageSize) : posts;

  if (display.length === 0) {
    if (emptyFallback !== undefined && emptyFallback !== null) {
      return emptyFallback;
    }
    return (
      <section className="section-marketing">
        <header className="section-heading-group">
          <h1 className="section-heading">{resolvedHeading}</h1>
          {subheading ? <p className="section-description">{subheading}</p> : null}
        </header>
        <p className="section-description post-empty">{s.emptyIndex}</p>
      </section>
    );
  }

  return (
    <section className="section-marketing post-list">
      <header className="section-heading-group">
        <h1 className="section-heading">{resolvedHeading}</h1>
        {subheading ? <p className="section-description">{subheading}</p> : null}
      </header>
      <ul className="post-grid feature-grid">
        {display.map((post) => (
          <li key={post.slug}>
            <BlogPostCard
              post={post}
              hrefPrefix={hrefPrefix}
              readingMinutesLabel={resolvedReadingMinutesLabel}
              locale={locale}
            />
          </li>
        ))}
      </ul>
      <BlogPagination
        currentOffset={offset}
        pageSize={pageSize}
        totalKnownCount={totalKnownCount}
        hasMore={hasMore}
        onPrev={onPrev}
        onNext={onNext}
        hrefForPage={(page) => buildBlogPageHref(hrefPrefix, page)}
      />
    </section>
  );
}

BlogIndexPage.propTypes = {
  client: PropTypes.shape({
    listBlogPosts: PropTypes.func.isRequired,
  }).isRequired,
  initialPosts: PropTypes.array,
  initialOffset: PropTypes.number,
  initialPage: PropTypes.number,
  pageSize: PropTypes.number,
  totalKnownCount: PropTypes.number,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  loadingFallback: PropTypes.node,
  errorFallback: PropTypes.func,
  emptyFallback: PropTypes.node,
  readingMinutesLabel: PropTypes.string,
  hrefPrefix: PropTypes.string,
  locale: PropTypes.string,
  strings: PropTypes.object,
};
